import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useFormResponses } from "@/hooks/useFormResponses";
import { FieldHelpButton } from "@/components/FieldHelpButton";
import { cn } from "@/lib/utils";
import { combineFormError, runUpserts } from "@/lib/validation-errors";

/**
 * Step 8 — Oath of Allegiance willingness questions (Part 11 of N-400).
 *
 * These map to checkboxes on the form. Stored as booleans in form_responses
 * under keys read by n400_2025_01_20.go:
 *   willing_to_bear_arms
 *   willing_to_noncombatant_service
 *   willing_to_civilian_service
 *
 * USCIS guidance: a "no" to any of these doesn't auto-disqualify, but
 * may trigger additional questions at interview. We don't gate the user
 * here — just collect honest answers.
 */

interface Question {
  key: string;
  label: string;
  helper: string;
  helpQuestion?: string;
  helpFieldKey?: string;
}

const QUESTIONS: Question[] = [
  {
    key: "willing_to_bear_arms",
    label: "Are you willing to bear arms on behalf of the United States if required by law?",
    helper:
      "This is a standard naturalization question. Religious objectors or others who answer 'no' may be asked follow-up questions at their interview.",
    helpQuestion: "What happens if I answer 'No' to bearing arms? Will that hurt my application?",
    helpFieldKey: "willing_to_bear_arms",
  },
  {
    key: "willing_to_noncombatant_service",
    label:
      "Are you willing to perform noncombatant services in the U.S. Armed Forces if required by law?",
    helper:
      "Noncombatant service includes roles like medic or chaplain — military service that doesn't involve combat.",
  },
  {
    key: "willing_to_civilian_service",
    label: "Are you willing to perform work of national importance under civilian direction if required by law?",
    helper:
      "Civilian alternative service for those who object to military service of any kind, e.g. with FEMA or the Forest Service.",
  },
];

export function StepOath() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const fr = useFormResponses(id);

  const [answers, setAnswers] = useState<Record<string, boolean | null>>({});

  useEffect(() => {
    if (fr.isLoading) return;
    const next: Record<string, boolean | null> = {};
    for (const q of QUESTIONS) {
      const v = fr.get<boolean>(q.key);
      next[q.key] = typeof v === "boolean" ? v : null;
    }
    setAnswers(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fr.isLoading, ...QUESTIONS.map((q) => fr.get(q.key))]);

  const allAnswered = QUESTIONS.every((q) => typeof answers[q.key] === "boolean");

  const [saveErrors, setSaveErrors] = useState<unknown[]>([]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!allAnswered) return;
    setSaveErrors([]);
    const errors = await runUpserts(
      QUESTIONS.map((q) =>
        fr.upsert.mutateAsync({ fieldKey: q.key, value: answers[q.key] }),
      ),
    );
    if (errors.length > 0) {
      setSaveErrors(errors);
      return;
    }
    navigate(`/cases/${id}/wizard/contact`);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Oath of Allegiance</h1>
        <p className="text-sm text-slate-500 mt-1">
          When you naturalize you take an Oath of Allegiance. These three
          questions are required by USCIS — answer honestly.
        </p>
      </header>

      <div className="space-y-4">
        {QUESTIONS.map((q) => (
          <fieldset
            key={q.key}
            className="bg-white rounded-xl border border-slate-200 p-5"
          >
            <legend className="font-medium flex flex-wrap items-center gap-x-1">
              {q.label}
              {q.helpQuestion && (
                <FieldHelpButton question={q.helpQuestion} fieldKey={q.helpFieldKey} />
              )}
            </legend>
            <p className="text-sm text-slate-500 mt-1">{q.helper}</p>
            <div className="flex gap-3 mt-4">
              {[
                { v: true, label: "Yes" },
                { v: false, label: "No" },
              ].map(({ v, label }) => {
                const selected = answers[q.key] === v;
                return (
                  <button
                    key={label}
                    type="button"
                    onClick={() => setAnswers((a) => ({ ...a, [q.key]: v }))}
                    className={cn(
                      "px-5 py-2 rounded-lg border text-sm font-medium transition-colors",
                      selected
                        ? "bg-slate-900 text-white border-slate-900"
                        : "bg-white text-slate-700 border-slate-300 hover:border-slate-400",
                    )}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </fieldset>
        ))}
      </div>

      {/* Errors from any of the parallel upserts. saveErrors is the
          collected set — fr.upsert.error alone would race away in the
          Promise.allSettled chain. */}
      {combineFormError(saveErrors) && (
        <p className="text-sm text-red-600">
          Could not save: {combineFormError(saveErrors)}
        </p>
      )}

      <div className="flex items-center justify-between pt-4 border-t border-slate-200">
        <Link
          to={`/cases/${id}/wizard/criminal`}
          className="text-sm text-slate-600 hover:text-slate-900"
        >
          ← Background
        </Link>
        <button
          type="submit"
          disabled={!allAnswered || fr.upsert.isPending}
          className="rounded-xl bg-slate-900 text-white px-5 py-2.5 text-sm font-medium hover:bg-slate-800 disabled:bg-slate-300"
        >
          {fr.upsert.isPending ? "Saving…" : "Save & continue"}
        </button>
      </div>
    </form>
  );
}
